import { Link } from 'react-router-dom';
import { DukantiBrandMark, useTranslation } from '@dukanti/shared';
import { Bell, LogOut, Plus } from 'lucide-react';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { useIncomingOrdersCount } from '@/hooks/use-incoming-orders-count';
import { cn } from '@/lib/utils';
import { AdminSignOutDialog } from './admin-sign-out-dialog';
import { LanguageSwitcher } from './language-switcher';

/**
 * Persistent admin top bar: hamburger (mobile-only) + brand mark/wordmark +
 * reserved user-menu slot + language switcher. The sidebar state is owned
 * by shadcn's SidebarProvider, so this component only reads `open` from
 * useSidebar() to compute the translated aria-label on the trigger.
 *
 * Brand wordmark is hardcoded "Dukanti" (product mark, not translated).
 * Spacing uses logical Tailwind props (`me-2`) so the hamburger sits on
 * the content-start edge under both LTR and RTL.
 */
interface AdminShellTopbarProps {
  userId: string | null;
}

export function AdminShellTopbar({ userId }: AdminShellTopbarProps) {
  const { t } = useTranslation();
  const { open } = useSidebar();
  const incomingOrders = useIncomingOrdersCount(userId);

  return (
    <header
      data-testid="admin-shell-nav"
      className="border-border bg-card/95 sticky top-0 z-40 border-b backdrop-blur"
    >
      <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between gap-3 px-3 sm:h-16 sm:px-6">
        <div className="flex min-w-0 items-center">
          <SidebarTrigger
            data-testid="admin-shell-sidebar-trigger"
            className="me-2 md:hidden"
            aria-label={t(open ? 'admin.nav.closeSidebar' : 'admin.nav.openSidebar')}
          />
          <Link
            to="/dashboard"
            data-testid="admin-shell-brand-link"
            className="font-display text-foreground flex min-w-0 items-center gap-2 text-start text-xl font-semibold tracking-tight"
          >
            <DukantiBrandMark className="size-8 rounded-md" />
            <span className="truncate">Dukanti</span>
          </Link>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/orders"
            data-testid="admin-shell-orders-shortcut"
            aria-label={t('admin.nav.ordersShortcut')}
            className="border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring relative inline-flex size-9 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          >
            <Bell className="size-4" aria-hidden="true" />
            {incomingOrders.count > 0 && (
              <span
                className={cn(
                  'bg-sidebar-primary text-sidebar-primary-foreground absolute -end-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none',
                )}
                aria-hidden="true"
              >
                {incomingOrders.displayCount}
              </span>
            )}
          </Link>
          <Link
            to="/products/new"
            data-testid="admin-shell-new-product-link"
            aria-label={t('admin.nav.addProductAria')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring inline-flex h-9 items-center justify-center gap-2 rounded-md px-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 sm:px-3"
          >
            <Plus className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline">{t('admin.nav.addProduct')}</span>
            <span className="sr-only sm:hidden">{t('admin.nav.addProduct')}</span>
          </Link>
          <LanguageSwitcher />
          <AdminSignOutDialog
            triggerTestId="admin-shell-topbar-sign-out"
            triggerClassName="border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring inline-flex size-9 items-center justify-center rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          >
            <LogOut className="size-4" aria-hidden="true" />
            <span className="sr-only">{t('admin.nav.signOut')}</span>
          </AdminSignOutDialog>
        </div>
      </div>
    </header>
  );
}
